import {
    PropsWithChildren,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo
} from "react";

import {
    GrowthUnifiedProfileClient,
    TeamTypeOptions,
    JTBDOptions,
    OptionalFields,
    GrowthUnifiedProfile,
    AccountProfileOptionalFields,
    UserRecommendations
} from "@/libraries/growth-unified-profile-client";
import { useLoggedInUser } from "@/utils/logged-in-user-provider";

import { useAnalyticsClient } from "../../../../../../../analytics/wac-analytics/analytics-client";
import {
    USER_SEGMENTATION_TEAM_TYPE_CUSTOMER_SERVICE,
    USER_SEGMENTATION_TEAM_TYPE_DATA_SCIENCE,
    USER_SEGMENTATION_TEAM_TYPE_DESIGN,
    USER_SEGMENTATION_TEAM_TYPE_FINANCE,
    USER_SEGMENTATION_TEAM_TYPE_HR,
    USER_SEGMENTATION_TEAM_TYPE_IT_SUPPORT,
    USER_SEGMENTATION_TEAM_TYPE_LEGAL,
    USER_SEGMENTATION_TEAM_TYPE_MARKETING,
    USER_SEGMENTATION_TEAM_TYPE_OPERATIONS,
    USER_SEGMENTATION_TEAM_TYPE_OTHER_PERSONAL,
    USER_SEGMENTATION_TEAM_TYPE_PRODUCT_MANAGEMENT,
    USER_SEGMENTATION_TEAM_TYPE_PROJECT_MANAGEMENT,
    USER_SEGMENTATION_TEAM_TYPE_SALES,
    USER_SEGMENTATION_TEAM_TYPE_SOFTWARE,
    UserSegmentationTeamTypes
} from "../models/user-segmentation";
import { checkboxValues } from "../views/jtbd/constants";

import { useAnchorLocalStorageTeamType } from "./use-anchor-local-storage-team-type";

export const userSegToGupsTeamTypeMap: { [key: string]: TeamTypeOptions } = {
    [USER_SEGMENTATION_TEAM_TYPE_SOFTWARE]: "SOFTWARE_DEVELOPMENT",
    [USER_SEGMENTATION_TEAM_TYPE_IT_SUPPORT]: "IT_SUPPORT",
    [USER_SEGMENTATION_TEAM_TYPE_DESIGN]: "DESIGN",
    [USER_SEGMENTATION_TEAM_TYPE_CUSTOMER_SERVICE]: "CUSTOMER_SERVICE",
    [USER_SEGMENTATION_TEAM_TYPE_HR]: "HUMAN_RESOURCES",
    [USER_SEGMENTATION_TEAM_TYPE_OPERATIONS]: "OPERATIONS",
    [USER_SEGMENTATION_TEAM_TYPE_SALES]: "SALES",
    [USER_SEGMENTATION_TEAM_TYPE_FINANCE]: "FINANCE",
    [USER_SEGMENTATION_TEAM_TYPE_LEGAL]: "LEGAL",
    [USER_SEGMENTATION_TEAM_TYPE_OTHER_PERSONAL]: "OTHER",
    [USER_SEGMENTATION_TEAM_TYPE_MARKETING]: "MARKETING",
    [USER_SEGMENTATION_TEAM_TYPE_DATA_SCIENCE]: "DATA_SCIENCE",
    [USER_SEGMENTATION_TEAM_TYPE_PRODUCT_MANAGEMENT]: "PRODUCT_MANAGEMENT",
    [USER_SEGMENTATION_TEAM_TYPE_PROJECT_MANAGEMENT]: "PROJECT_MANAGEMENT",
    "software-engineering": "SOFTWARE_ENGINEERING"
};

export type SupportedGupsTeamTypes = keyof typeof userSegToGupsTeamTypeMap;

export const getGupsJtbd = (jtbd: checkboxValues[]): JTBDOptions[] => {
    return jtbd.filter(({ isChecked }) => isChecked).map(({ value }) => value);
};
export const getGupsTeamType = (
    teamType: SupportedGupsTeamTypes
): TeamTypeOptions | undefined => {
    for (const [key, value] of Object.entries(userSegToGupsTeamTypeMap)) {
        if (key === teamType) {
            return value;
        }
    }
    return undefined;
};

type GrowthUnifiedProfileResponse = {
    data?: GrowthUnifiedProfile;
    error?: unknown;
};

export type GupsStatus = {
    readonly addTeamTypeCallback: (
        selectedTeamType: UserSegmentationTeamTypes
    ) => Promise<unknown>;
    readonly getUserProfileJobFunctionCallback: (useCase?: string) => Promise<{
        requestInProgress: boolean;
        data?: GrowthUnifiedProfile["userProfile"];
        error: unknown;
    }>;
    readonly getFullProfileCallback: () => Promise<{
        data?: GrowthUnifiedProfile;
        error: unknown;
    }>;
    readonly addJtbdCallback: (
        jtbd: checkboxValues[],
        template?: string
    ) => Promise<unknown>;
    readonly addTemplateCallback: (template: string) => Promise<unknown>;
    readonly addTenantIdCallback: (tenantId: string) => Promise<unknown>;
    readonly getProfileCallback: (
        abortSignal?: AbortSignal
    ) => Promise<GrowthUnifiedProfileResponse | unknown>;
    readonly getProfileSitesCallback: (
        tenantId: string
    ) => Promise<GrowthUnifiedProfileResponse | undefined>;
    readonly linkAccountToAnonymousIdCallback: () => Promise<unknown>;
    readonly addTemplateCardCallback: (templateId: string) => Promise<unknown>;
    readonly getAnonProfileSitesCallback: (
        anonymousId: string,
        useCase?: string
    ) => Promise<GrowthUnifiedProfileResponse | undefined>;
    readonly getAccountProfileRecommendationsCallback: (
        accoundId: string,
        useCase?: string
    ) => Promise<{
        data?: UserRecommendations;
        error: unknown;
    }>;
    readonly addMatureExampleProjectIdsCallback: (
        matureExampleProjectIds: string[],
        tenantId: string
    ) => Promise<unknown>;
};

export function useGupsStatus() {
    return useContext(GupsContext);
}

const GupsContext = createContext<GupsStatus>({
    addTeamTypeCallback: async () => undefined,
    getUserProfileJobFunctionCallback: async () => ({
        requestInProgress: true,
        data: undefined,
        error: undefined
    }),
    getFullProfileCallback: async () => ({
        data: undefined,
        error: undefined
    }),
    addJtbdCallback: async () => undefined,
    addTemplateCallback: async () => undefined,
    addTenantIdCallback: async () => undefined,
    getProfileSitesCallback: async () => undefined,
    linkAccountToAnonymousIdCallback: async () => undefined,
    addTemplateCardCallback: async () => undefined,
    getProfileCallback: async () => undefined,
    getAnonProfileSitesCallback: async () => undefined,
    getAccountProfileRecommendationsCallback: async () => ({
        data: undefined,
        error: undefined
    }),
    addMatureExampleProjectIdsCallback: async () => undefined
});

export const GupsProvider = ({ children }: PropsWithChildren) => {
    const gupsClient = useMemo(() => new GrowthUnifiedProfileClient(), []);
    const { accountId } = useLoggedInUser();
    const client = useAnalyticsClient();
    const anonymousId = useMemo(
        () => client?.getAnonymousId() ?? undefined,
        [client]
    );

    const teamTypeFromLocalStorage = useAnchorLocalStorageTeamType();

    const addTeamTypeCallback = useCallback(
        async (selectedTeamType: SupportedGupsTeamTypes) => {
            try {
                const gupsTeamType = getGupsTeamType(selectedTeamType);

                if (!gupsTeamType) {
                    return {
                        error: {
                            message: `There is not a GUPS mapping for team type ${selectedTeamType}`
                        }
                    };
                }

                const { data: updatedProfile, error } =
                    await gupsClient.updateProfile({
                        profile: {
                            accountId,
                            anonymousId,
                            unifiedProfile: {
                                onboardingContext: {
                                    jira: {
                                        teamType: gupsTeamType
                                    }
                                }
                            }
                        }
                    });
                return { updatedProfile, error };
            } catch (error) {
                console.error("Error updating user profile in GUPS", error);
                return { error };
            }
        },
        [gupsClient, accountId, anonymousId]
    );

    const getUserProfileJobFunctionCallback = useCallback(
        async (useCase?: string) => {
            if (!accountId) {
                return {
                    requestInProgress: true,
                    data: undefined,
                    error: undefined
                };
            }

            const { data: profile, error } = await gupsClient.getProfile(
                {
                    accountId
                },
                [OptionalFields.USER_PROFILE_JOB_FUNCTION],
                useCase
            );
            return {
                requestInProgress: false,
                data: profile?.userProfile,
                error
            };
        },
        [gupsClient, accountId]
    );

    const getFullProfileCallback = useCallback(async () => {
        const { data: profile, error } = await gupsClient.getProfile(
            {
                accountId
            },
            [
                OptionalFields.USER_PROFILE,
                OptionalFields.USER_FOOTPRINTS,
                OptionalFields.SITES,
                OptionalFields.ONBOARDING_CONTEXT
            ]
        );
        return { data: profile, error };
    }, [gupsClient, accountId]);

    const addJtbdCallback = useCallback(
        async (jtbd: checkboxValues[], template?: string) => {
            try {
                const gupsJtbd = getGupsJtbd(jtbd);

                const { data: updatedProfile, error } =
                    await gupsClient.updateProfile({
                        profile: {
                            accountId,
                            anonymousId,
                            unifiedProfile: {
                                onboardingContext: {
                                    jira: {
                                        jobsToBeDone: gupsJtbd,
                                        template: template
                                    }
                                }
                            }
                        }
                    });
                return { updatedProfile, error };
            } catch (error) {
                console.error("Error updating user profile in GUPS", error);
                return { error };
            }
        },
        [gupsClient, accountId, anonymousId]
    );

    const addTemplateCallback = useCallback(
        async (template: string) => {
            try {
                const { data: updatedProfile, error } =
                    await gupsClient.updateProfile({
                        profile: {
                            accountId,
                            anonymousId,
                            unifiedProfile: {
                                onboardingContext: {
                                    jira: {
                                        template: template
                                    }
                                }
                            }
                        }
                    });
                return { updatedProfile, error };
            } catch (error) {
                console.error("Error updating user profile in GUPS", error);
                return { error };
            }
        },
        [gupsClient, accountId, anonymousId]
    );

    const addTenantIdCallback = useCallback(
        async (tenantId: string) => {
            try {
                const { data: updatedProfile, error } =
                    await gupsClient.updateProfile({
                        profile: {
                            accountId,
                            tenantId,
                            unifiedProfile: {}
                        }
                    });
                return { updatedProfile, error };
            } catch (error) {
                console.error("Error updating user profile in GUPS", error);
                return { error };
            }
        },
        [gupsClient, accountId]
    );

    const getProfileCallback = useCallback(
        async (abortSignal?: AbortSignal) => {
            try {
                const { data: profile, error } = await gupsClient.getProfile(
                    {
                        anonymousId
                    },
                    [OptionalFields.PAID_CHANNEL_CONTEXT],
                    undefined, // optionalHeaderUseCase
                    abortSignal
                );
                return { data: profile, error };
            } catch (error) {
                // Don't log AbortErrors as they're expected when requests are cancelled
                if (error instanceof Error && error.name !== "AbortError") {
                    console.error("Error getting GUPS profile", error);
                }
                return { error };
            }
        },
        [anonymousId, gupsClient]
    );

    const getProfileSitesCallback = useCallback(
        async (accountId: string) => {
            try {
                const { data: profile, error } = await gupsClient.getProfile(
                    {
                        accountId
                    },
                    [OptionalFields.SITES]
                );
                return { data: profile, error };
            } catch (error) {
                console.error("Error getting profile sites GUPS", error);
                return { error };
            }
        },
        [gupsClient]
    );

    const linkAccountToAnonymousIdCallback = useCallback(async () => {
        try {
            if (accountId && anonymousId) {
                const { data: updatedProfile, error } =
                    await gupsClient.updateProfile({
                        profile: {
                            accountId,
                            anonymousId,
                            unifiedProfile: {}
                        }
                    });
                return { updatedProfile, error };
            } else {
                console.info(
                    `Account ID or Anonymous ID is missing, ${accountId} ${anonymousId}`
                );
                return;
            }
        } catch (error) {
            console.error("Error updating user profile in GUPS", error);
            return { error };
        }
    }, [gupsClient, accountId, anonymousId]);

    const getAnonProfileSitesCallback = useCallback(
        async (anonymousId: string, useCase?: string) => {
            try {
                const { data: profile, error } = await gupsClient.getProfile(
                    {
                        anonymousId
                    },
                    [OptionalFields.SITES],
                    useCase
                );
                return { data: profile, error };
            } catch (error) {
                console.error("Error getting profile sites GUPS", error);
                return { error };
            }
        },
        [gupsClient]
    );

    const addTemplateCardCallback = useCallback(
        async (templateId: string) => {
            try {
                const { data: updatedProfile, error } =
                    await gupsClient.updateProfile({
                        profile: {
                            accountId,
                            anonymousId,
                            unifiedProfile: {
                                onboardingContext: {
                                    confluence: {
                                        template: templateId
                                    }
                                }
                            }
                        }
                    });
                return { updatedProfile, error };
            } catch (error) {
                return { error };
            }
        },
        [gupsClient, accountId, anonymousId]
    );

    const getAccountProfileRecommendationsCallback = useCallback(
        async (accountId: string, useCase?: string) => {
            try {
                const { data: profile, error } =
                    await gupsClient.getAccountProfile(
                        {
                            accountId
                        },
                        [AccountProfileOptionalFields.RECOMMENDATIONS],
                        useCase
                    );
                return {
                    data: profile?.recommendations,
                    error
                };
            } catch (error) {
                console.error(
                    "Error getting account profile recommendations GUPS",
                    error
                );
                return { error };
            }
        },
        [gupsClient]
    );

    const addMatureExampleProjectIdsCallback = useCallback(
        async (matureExampleProjectIds: string[], tenantId: string) => {
            try {
                const { data: updatedProfile, error } =
                    await gupsClient.updateProfile({
                        profile: {
                            accountId,
                            anonymousId,
                            tenantId,
                            unifiedProfile: {
                                onboardingContext: {
                                    jira: {
                                        matureExampleProjectIds:
                                            matureExampleProjectIds ?? []
                                    }
                                }
                            }
                        }
                    });
                return { updatedProfile, error };
            } catch (error) {
                console.error("Error updating user profile in GUPS", error);
                return { error };
            }
        },
        [gupsClient, accountId, anonymousId]
    );

    useEffect(() => {
        if (teamTypeFromLocalStorage) {
            addTeamTypeCallback(teamTypeFromLocalStorage);
        }
    }, [teamTypeFromLocalStorage, addTeamTypeCallback]);

    return (
        <GupsContext.Provider
            value={{
                addTeamTypeCallback,
                getUserProfileJobFunctionCallback,
                getAccountProfileRecommendationsCallback,
                getFullProfileCallback,
                addJtbdCallback,
                addTemplateCallback,
                getProfileSitesCallback,
                addTenantIdCallback,
                linkAccountToAnonymousIdCallback,
                addTemplateCardCallback,
                getProfileCallback,
                getAnonProfileSitesCallback,
                addMatureExampleProjectIdsCallback
            }}
        >
            {children}
        </GupsContext.Provider>
    );
};
interface MockGupsProviderProps extends PropsWithChildren {
    // @ts-ignore
    mockResponse;
}
export const MockGupsProvider = ({
    mockResponse,
    children
}: MockGupsProviderProps) => {
    const addTeamTypeCallback = async () => undefined;
    const getUserProfileJobFunctionCallback = async () => ({
        requestInProgress: true,
        data: undefined,
        error: undefined
    });
    const getFullProfileCallback = async () => ({
        data: undefined,
        error: undefined
    });
    const addJtbdCallback = async () => undefined;
    const addTemplateCallback = async () => undefined;
    const addTenantIdCallback = async () => undefined;
    const getProfileSitesCallback = async () => undefined;
    const linkAccountToAnonymousIdCallback = async () => undefined;
    const addTemplateCardCallback = async () => undefined;
    const getProfileCallback = async () => undefined;
    const getAnonProfileSitesCallback = async () => {
        return Promise.resolve(mockResponse);
    };
    const getAccountProfileRecommendationsCallback = async () => ({
        data: undefined,
        error: undefined
    });

    const addMatureExampleProjectIdsCallback = async () => undefined;

    return (
        <GupsContext.Provider
            value={{
                addTeamTypeCallback,
                getUserProfileJobFunctionCallback,
                getFullProfileCallback,
                addJtbdCallback,
                addTemplateCallback,
                getProfileSitesCallback,
                addTenantIdCallback,
                linkAccountToAnonymousIdCallback,
                addTemplateCardCallback,
                getProfileCallback,
                getAnonProfileSitesCallback,
                getAccountProfileRecommendationsCallback,
                addMatureExampleProjectIdsCallback
            }}
        >
            {children}
        </GupsContext.Provider>
    );
};
